import { useEffect, useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { directionOf, playPageEnter } from '../lib/pageTransition';
import { motionFlags } from '../lib/motionFlags';
import EventsList from './EventsList';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';
import { setChosenHues } from '../lib/map/faceStyle';
import { fetchFestivalSetIndex } from '../api/festivalSets';
import { queryKeys } from '../lib/query/queryKeys';
import { STALE, GC } from '../lib/query/staleTimes';
import { fetchEventLineup, fetchMyArtistVotes } from '../api/artistVotes';
import { useFestivalSets } from '../hooks/queries/useFestivalSets';
import { useMyRsvps } from '../hooks/queries/useMyRsvps';
import {
  CurrentUserProfileProvider,
  useCurrentUserProfile,
} from '../contexts/CurrentUserProfileContext';
import { usePrefetchFestivalMapsForToday } from '../hooks/queries/usePrefetchFestivalMapsForToday';
import { useMyFestivalMaps } from '../hooks/queries/useMyFestivalMaps';
import { usePrefetchEventDay } from '../hooks/queries/usePrefetchEventDay';
import { FindProximityProvider, useFindProximity } from '../contexts/FindProximityContext';
import LiveActivityDriver from './LiveActivityDriver';
import { loadFestivalMap } from '../lib/festivalOffline';
import { FESTIVAL_MAPS_ENABLED } from '../config/festivalMaps';
import AuthQueryCacheSync from '../lib/query/AuthQueryCacheSync';
import { getGlassSurfaceRootClasses } from '../config/glassSurfaces';
import AmbientOrbField from './AmbientOrbField';
import LaunchSplash from './motion/LaunchSplash';
import { whenQuiet } from '../lib/quietMoment';
import ConnectivityBanner from './ui/ConnectivityBanner';
import Header from './Header';
import NotificationToast from './NotificationToast';
import GetAppPrompt from './GetAppPrompt';
import { installKeyboardScrollReset, resetDocumentScrollIfIdle } from '../lib/keyboardScrollReset';
import { appLog } from '../lib/appLog';
import { AuthPromptProvider } from './auth/AuthPrompt';
import NeedsDisplayName from './auth/NeedsDisplayName';
import WelcomeBar from './onboarding/WelcomeBar';
import PushHandler from './PushHandler';
import SelfLookPicker from './dev/SelfLookPicker';
import { AppNowPlayingBar } from './lineup/NowPlayingBar';

/** The map of a festival you are at this weekend is fetched before you ask for it (see the hook). Renders nothing. */
/**
 * On an event day (a live Find session), the event's map scene is built a few seconds after launch while the phone is
 * idle, so Maps opens on a finished map (Stage 9, 2026-10-21). Once per event per app run; never while a map is on screen.
 */
const prebuiltFor = new Set();
/** An event of yours whose day it is: from 3 h before it starts until it ends (ends_at, else 6 h after the start) - the
 *  presence window of the location policy. The soonest one. */
function todaysEventId(mine) {
  const now = Date.now();
  let best = null;
  for (const m of mine || []) {
    const start = m.event_datetime ? Date.parse(m.event_datetime) : NaN;
    if (!Number.isFinite(start)) continue;
    const end = m.ends_at ? Date.parse(m.ends_at) : start + 6 * 3600e3;
    if (now < start - 3 * 3600e3 || now > end) continue;
    if (!best || start < best.start) best = { id: m.event_id, start };
  }
  return best?.id ?? null;
}
function FestivalMapPrebuild() {
  const { sessionEventId } = useFindProximity();
  const { session } = useCurrentUserProfile();
  // any map of yours whose event is today, not only a live Find session (2026-10-31): the first open on a show day is the
  // one that must be instant, and it comes before Find is on
  const { data: mine } = useMyFestivalMaps(
    FESTIVAL_MAPS_ENABLED ? (session?.user?.id ?? null) : null,
  );
  const todayId = todaysEventId(mine);
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const onMap = pathname.startsWith('/map');
  useEffect(() => {
    // ?prebuild=<eventId> (dev): as if that event's Find session were live, to check the prebuild without one
    const devEvent = import.meta.env.DEV
      ? new URLSearchParams(window.location.search).get('prebuild')
      : null;
    const eventId = sessionEventId ?? todayId ?? devEvent;
    const why = sessionEventId ? 'session' : todayId ? 'today' : 'dev';
    if (!FESTIVAL_MAPS_ENABLED || !eventId || onMap || prebuiltFor.has(eventId)) return undefined;
    let cancelled = false;
    const run = async () => {
      if (cancelled || document.hidden) return;
      prebuiltFor.add(eventId);
      try {
        const payload = await queryClient.fetchQuery({
          queryKey: queryKeys.festivalMap.published(eventId),
          queryFn: () => loadFestivalMap(eventId),
          staleTime: Infinity,
          networkMode: 'offlineFirst',
        });
        const scene3d = payload?.properties?.terrain?.scene3d;
        if (!scene3d || cancelled) return;
        const [{ prebuildScene, sceneKeyFor }, { geoKeyFor, loadGeo, geoSession }] =
          await Promise.all([import('./find/FestivalMap3D'), import('../lib/map3d/geoCache')]);
        const key = geoKeyFor(eventId, payload);
        const geo = key ? geoSession(key, await loadGeo(key, 2000)) : null; // off the critical path: it may take its time
        if (!cancelled && !document.hidden && !window.location.pathname.startsWith('/map'))
          prebuildScene(scene3d, { eventId, geo, key: sceneKeyFor(payload, eventId), why });
      } catch {
        /* a prebuild is a nicety: opening the map builds it as before */
      }
    };
    // Only in a quiet moment (2026-10-30 phone logs: the Bowl's prebuild ran 5 s after launch, while Events was being
    // scrolled, and held the screen 16 s): 12 s after the app opened and 4 s with nothing touched (lib/quietMoment).
    const stop = whenQuiet(run, { quietMs: 4000, afterMs: 12000 });
    return () => {
      cancelled = true;
      stop();
    };
  }, [sessionEventId, todayId, onMap, queryClient]);
  return null;
}

function FestivalMapPrefetch() {
  const { session } = useCurrentUserProfile();
  usePrefetchFestivalMapsForToday(session?.user?.id ?? null);
  usePrefetchEventDay(session?.user?.id ?? null); // groups and Sets too, map or not (Sam, 2026-10-22)
  return null;
}

/**
 * The ring colours people picked (Edit Profile), fetched at launch and every 10 min so faces everywhere use them
 * (lib/map/faceStyle.js). The last set is kept on the phone, so faces have them before this answers. Renders nothing.
 */
function PeopleHues() {
  const { session } = useCurrentUserProfile();
  const userId = session?.user?.id ?? null;
  const { data } = useQuery({
    queryKey: queryKeys.people.hues(userId),
    queryFn: async () => {
      const { data: rows, error } = await supabase.rpc('get_people_hues');
      if (error) throw error;
      return rows ?? [];
    },
    enabled: Boolean(userId),
    staleTime: STALE.user,
  });
  useEffect(() => {
    if (data) setChosenHues(data);
  }, [data]);
  return null;
}

/**
 * The Sets list is fetched at launch, so the tab opens on data rather than a spinner (operator, 2026-09-23: "took
 * a second to load"). The query itself is sub-millisecond; the wait was the first round trip. Renders nothing.
 */
function SetsIndexPrefetch() {
  const queryClient = useQueryClient();
  const { session, authReady } = useCurrentUserProfile();
  const userId = session?.user?.id ?? null;
  const { data: festivals } = useFestivalSets();
  const { data: rsvps } = useMyRsvps(userId, { enabled: authReady });
  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.sets.index(),
      queryFn: fetchFestivalSetIndex,
      staleTime: STALE.semiStatic,
    });
  }, [queryClient]);
  // The lineup (set times) and your picks for every festival you are going to, downloaded ahead so Sets opens with
  // no service (operator, 2026-09-23). Small - tens of KB each - and kept with the maps in the persisted cache.
  useEffect(() => {
    if (!festivals?.length || !rsvps) return;
    const going = new Set(rsvps.filter((r) => r.status === 'going').map((r) => r.event_id));
    for (const f of festivals) {
      if (!going.has(f.event_id)) continue;
      queryClient.prefetchQuery({
        queryKey: queryKeys.events.lineup(f.event_id),
        queryFn: () => fetchEventLineup(f.event_id),
        staleTime: STALE.semiStatic,
        gcTime: GC.festival,
      });
      if (userId) {
        queryClient.prefetchQuery({
          queryKey: queryKeys.events.myArtistVotes(f.event_id, userId),
          queryFn: () => fetchMyArtistVotes(f.event_id, userId),
          staleTime: STALE.user,
          gcTime: GC.festival,
        });
      }
    }
  }, [festivals, rsvps, userId, queryClient]);
  return null;
}

/**
 * Scroll position per page for Sets and Groups, for this app session: switch tabs and come back to exactly where you
 * were (operator, 2026-09-23). Events keeps its own feed restore; other pages start at the top as before.
 */
const REMEMBER_SCROLL = /^\/(sets|groups)(\/|$)/;
const scrollByPath = new Map();
function useScrollMemory(mainRef) {
  const location = useLocation();
  const key = `${location.pathname}${location.search}`;
  const keyRef = useRef(key);
  keyRef.current = key;
  const restoringRef = useRef(false);

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return undefined;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!restoringRef.current && REMEMBER_SCROLL.test(keyRef.current))
          scrollByPath.set(keyRef.current, el.scrollTop);
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('scroll', onScroll);
    };
  }, [mainRef]);

  useLayoutEffect(() => {
    const el = mainRef.current;
    const saved = REMEMBER_SCROLL.test(key) ? scrollByPath.get(key) : undefined;
    if (!el || !saved) return undefined;
    // The page re-renders as it fills in (lineup, group, grid), and a momentarily short page clamps the scroll to 0.
    // So for 1.2 s the saved position is held - re-applied whenever it slips - unless you touch or scroll yourself,
    // and nothing is saved meanwhile (that 0 was overwriting the memory).
    restoringRef.current = true;
    const started = performance.now();
    let raf = 0;
    const stop = () => {
      restoringRef.current = false;
      cancelAnimationFrame(raf);
    };
    const hold = () => {
      if (!restoringRef.current) return;
      if (el.scrollTop !== saved && el.scrollHeight - el.clientHeight >= saved)
        el.scrollTop = saved;
      if (performance.now() - started > 1200) {
        stop();
        return;
      }
      raf = requestAnimationFrame(hold);
    };
    hold();
    const userTook = () => stop();
    el.addEventListener('touchstart', userTook, { passive: true });
    el.addEventListener('wheel', userTook, { passive: true });
    return () => {
      stop();
      el.removeEventListener('touchstart', userTook);
      el.removeEventListener('wheel', userTook);
    };
  }, [key, mainRef]);
}

export default function Layout() {
  const mainRef = useRef(null);
  useScrollMemory(mainRef);
  const { pathname } = useLocation();
  const onEvents = pathname === '/';
  // The page arrives with a little motion instead of a snap (lib/pageTransition). Before paint, so the first frame of the
  // new page is already the animation's first frame. A replace (a redirect, e.g. /map -> /map/:event) is part of the
  // same move and does not animate again.
  const pageRef = useRef(null);
  const prevPathRef = useRef(pathname);
  const navType = useNavigationType();
  useLayoutEffect(() => {
    const from = prevPathRef.current;
    prevPathRef.current = pathname;
    if (from === pathname || navType === 'REPLACE') return;
    playPageEnter(pageRef.current, motionFlags().page, directionOf(from, pathname));
  }, [pathname, navType]);
  // ?tour=1 (dev, /lab/motion): walk the dock's tabs so a frame shows the page change without a finger.
  const navigate = useNavigate();
  useEffect(() => {
    if (!import.meta.env.DEV || !motionFlags().tour) return undefined;
    const stops = ['/', '/sets', '/map', '/groups', '/map', '/sets'];
    let i = 0;
    const t = setInterval(() => {
      i = (i + 1) % stops.length;
      navigate(stops[i]);
    }, 2200);
    return () => clearInterval(t);
  }, [navigate]);
  const eventsSeenRef = useRef(false);
  if (onEvents) eventsSeenRef.current = true;
  // The tab dock goes dead when iOS leaves the document offset after the keyboard (lib/keyboardScrollReset).
  useEffect(() => installKeyboardScrollReset(), []);
  // A route change can unmount a focused field mid-keyboard (tap a tab from search); settle it once the new page is up.
  useEffect(() => {
    appLog('info', 'nav.route', { to: pathname });
    const t = setTimeout(resetDocumentScrollIfIdle, 400);
    return () => clearTimeout(t);
  }, [pathname]);
  return (
    <CurrentUserProfileProvider>
      <AuthPromptProvider>
        <FindProximityProvider>
          <AuthQueryCacheSync />
          <FestivalMapPrefetch />
          <SelfLookPicker />
          <FestivalMapPrebuild />
          <SetsIndexPrefetch />
          <PeopleHues />
          <LiveActivityDriver />
          {/* overflow-y:hidden contains the dvh shell scroll; overflow-x:clip lets rail bleed reach viewport edges without escaping further */}
          <div
            className={`relative h-[100dvh] bg-gray-50 dark:bg-dark-bg transition-colors [overflow-x:clip] [overflow-y:hidden] ${getGlassSurfaceRootClasses()}`}
          >
            {/* On a map the orbs and sparks hold still: 75 animated, partly heavily blurred layers kept moving under the 3D map (and
            the glass header and dock re-blurred them every frame) - GPU time the map needed (audit 2026-10-22). Same look. */}
            <div
              className={`ambient-orb-layer${/^\/map\/[^/]+/.test(pathname) ? ' ambient-orb-layer--still' : ''}`}
              aria-hidden="true"
            >
              <AmbientOrbField />
            </div>
            <div id="app-layer" className="relative z-10 h-full min-h-0">
              <Header />
              <LaunchSplash />
              <ConnectivityBanner />
              {/* No overflow-x-hidden: negative-margin rails (e.g. Events trending) need to paint to the main edges */}
              <main
                ref={mainRef}
                className="h-full min-h-0 w-full min-w-0 overflow-y-auto"
                style={{ overscrollBehaviorY: 'contain', WebkitOverflowScrolling: 'touch' }}
              >
                <div
                  ref={pageRef}
                  className="app-page min-h-full pt-app-content pb-app-content md:pb-0"
                >
                  {/* Events stays mounted once seen and is only hidden on other tabs: coming back shows the page as it was,
                  images decoded and scrolled where you left it, instead of a rebuild (Sam, 2026-10-21). Its route ("/")
                  renders nothing. */}
                  {eventsSeenRef.current && (
                    <div style={onEvents ? undefined : { display: 'none' }}>
                      <EventsList active={onEvents} />
                    </div>
                  )}
                  <Outlet />
                </div>
              </main>
            </div>
            <NotificationToast />
            <GetAppPrompt />
          </div>
        </FindProximityProvider>
        <NeedsDisplayName />
        {/* The welcome flow's bar, on any page opened with ?demo=welcome (lib/welcomeDemo). */}
        <WelcomeBar />
        <PushHandler />
        <AppNowPlayingBar pathname={pathname} />
      </AuthPromptProvider>
    </CurrentUserProfileProvider>
  );
}
